import { loadPracticeSnapshot, setStudyStreak } from './practiceStore';
import {
  advanceStreak,
  applyFinish,
  emptyStudyStats,
  isStreakGoal,
  localDateKey,
  type StreakGoal,
  type StudyStats,
} from './studyStats';

const STORAGE_KEY = 'mns-study-stats-v1';
const pending = new Map<string, Promise<{ stats: StudyStats; added: number; already: boolean; repeated: boolean }>>();

function storage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

function session(): Storage | null {
  try {
    return sessionStorage;
  } catch {
    return null;
  }
}

export function readStudyStats(): StudyStats {
  const store = storage();
  const empty = emptyStudyStats();
  if (!store) return empty;
  try {
    const parsed = JSON.parse(store.getItem(STORAGE_KEY) || '{}') as Partial<StudyStats>;
    const finishes = Array.isArray(parsed.finishes)
      ? parsed.finishes
          .filter((finish) => finish && typeof finish.lessonId === 'string' && finish.lessonId)
          .map((finish) => ({
            lessonId: String(finish.lessonId),
            date: String(finish.date || ''),
            correctBlanks: Math.max(0, Math.floor(Number(finish.correctBlanks) || 0)),
            skipped: Boolean(finish.skipped),
          }))
      : [];
    const goal = typeof parsed.streakGoal === 'number' && isStreakGoal(parsed.streakGoal) ? parsed.streakGoal : null;
    const reminder = typeof parsed.reminderTime === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(parsed.reminderTime)
      ? parsed.reminderTime
      : null;
    return {
      finishes,
      current: Math.max(0, Math.floor(Number(parsed.current) || 0)),
      longest: Math.max(0, Math.floor(Number(parsed.longest) || 0)),
      lastDate: typeof parsed.lastDate === 'string' ? parsed.lastDate : null,
      freezesLeft: Math.max(0, Math.min(2, Math.floor(Number(parsed.freezesLeft ?? empty.freezesLeft)))),
      studyDates: Array.isArray(parsed.studyDates) ? parsed.studyDates.map(String) : [],
      freezeDates: Array.isArray(parsed.freezeDates) ? parsed.freezeDates.map(String) : [],
      streakGoal: goal,
      reminderTime: reminder,
      notificationsOn: Boolean(parsed.notificationsOn),
      freezePromptSeen: Boolean(parsed.freezePromptSeen),
    };
  } catch {
    return empty;
  }
}

export function writeStudyStats(stats: StudyStats): void {
  const store = storage();
  if (!store) return;
  store.setItem(STORAGE_KEY, JSON.stringify(stats));
}

function mergeBase(stats: StudyStats, practice: { current: number; longest: number; lastDate: string | null }): StudyStats {
  const practiceNewer = Boolean(practice.lastDate && (!stats.lastDate || practice.lastDate > stats.lastDate));
  const sameDay = practice.lastDate === stats.lastDate;
  const current = practiceNewer || (sameDay && practice.current > stats.current) ? practice.current : stats.current;
  return {
    ...stats,
    current,
    longest: Math.max(stats.longest, practice.longest, current),
    lastDate: practiceNewer ? practice.lastDate : stats.lastDate,
  };
}

/** Applies a lesson finish once per lesson per day in this tab. */
export function recordLessonFinish(
  userId: string | null,
  input: { lessonId: string; correctBlanks: number; skipped: boolean },
  today = localDateKey(new Date()),
): Promise<{ stats: StudyStats; added: number; already: boolean; repeated: boolean }> {
  const sessionKey = `${input.lessonId}:${today}`;
  const tab = session();
  if (tab?.getItem(`mns-finish-applied:${sessionKey}`) === '1') {
    return Promise.resolve({ stats: readStudyStats(), added: 0, already: true, repeated: true });
  }
  const existing = pending.get(sessionKey);
  if (existing) return existing;
  const promise = (async () => {
    const practice = await loadPracticeSnapshot(userId);
    const base = mergeBase(readStudyStats(), practice.streak);
    const applied = applyFinish(base, { ...input, date: today });
    writeStudyStats(applied.stats);
    await setStudyStreak(userId, {
      current: applied.stats.current,
      longest: applied.stats.longest,
      lastDate: applied.stats.lastDate,
    });
    tab?.setItem(`mns-finish-applied:${sessionKey}`, '1');
    return { stats: applied.stats, added: applied.added, already: applied.already, repeated: false };
  })().finally(() => {
    pending.delete(sessionKey);
  });
  pending.set(sessionKey, promise);
  return promise;
}

/** Marks today as a study day. A day that is already counted stays at the same streak. */
export async function recordStudyDay(userId: string | null, today = localDateKey(new Date())): Promise<StudyStats> {
  const practice = await loadPracticeSnapshot(userId);
  const base = mergeBase(readStudyStats(), practice.streak);
  const next = advanceStreak(base, today);
  writeStudyStats(next);
  await setStudyStreak(userId, {
    current: next.current,
    longest: next.longest,
    lastDate: next.lastDate,
  });
  return next;
}

export function setStreakGoal(goal: StreakGoal | null): StudyStats {
  const next = { ...readStudyStats(), streakGoal: goal };
  writeStudyStats(next);
  return next;
}

export function setReminderTime(time: string | null): StudyStats {
  const reminder = time && /^([01]\d|2[0-3]):[0-5]\d$/.test(time) ? time : null;
  const next = { ...readStudyStats(), reminderTime: reminder };
  writeStudyStats(next);
  return next;
}

export function setNotificationsOn(on: boolean): StudyStats {
  const next = { ...readStudyStats(), notificationsOn: on };
  writeStudyStats(next);
  return next;
}

export function setFreezePromptSeen(): StudyStats {
  const next = { ...readStudyStats(), freezePromptSeen: true };
  writeStudyStats(next);
  return next;
}
